import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CaseUtil from '@common/lib/caseUtil';
import { GTMTracking, Modal } from '@common/ui/general';
import {
  submitUserDetails, closeMissingDetails,
} from '@alumni_v2/stores/reviews/actions';
import { useMediaQuery } from '@common/hooks';
import {
  userDetailsSelector,
} from '@alumni_v2/selectors/reviews';
import { trackingMissingDetailsForm, trackingSubmitMissingDetails } from
  '@alumni_v2/utils/tracking';

import successIcon from '@alumni_v2/images/svg/tick.svg';
import tickIcon from '@alumni_v2/images/svg/simple-tick.svg';

import styles from './MissingDetails.module.scss';

function MissingDetails() {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    orgname: '',
    position: '',
    experience: '',
    linkedinProfile: '',
  });
  const { tablet } = useMediaQuery();

  const positions = [
    'Engineering Leadership',
    'Software Development Engineer (Backend)',
    'Software Development Engineer (Frontend)',
    'Software Development Engineer (Full Stack)',
    'Data Scientist',
    'Android Engineer',
    'iOS Engineer',
    'Devops Engineer',
    'Support Engineer',
    'Research Engineer',
    'Engineering Intern',
    'QA Engineer',
    'Co-founder',
    'SDET',
    'Program Manager',
    'Release Engineer',
    'Security Leadership',
    'Database Administrator',
    'Data Analyst',
    'Non Coder',
    'Other',
  ];

  const {
    showMissingDetailsModal, userDetails,
  } = useSelector(userDetailsSelector) || {};

  const handleChange = (event) => {
    const { name, value } = event.target;
    let validatedValue = value;
    if (name === 'experience') {
      if (Number.isNaN(value) || value < 0 || value > 999 || value.length > 3) {
        if (validatedValue < 0) {
          validatedValue = 0;
        } else {
          validatedValue = value.slice(0, 3);
        }
      }
    }
    setFormData((prevData) => ({ ...prevData, [name]: validatedValue }));
  };

  const handleCloseModal = useCallback(() => {
    dispatch(closeMissingDetails());
  }, [dispatch]);

  const {
    orgname, position, experience, linkedinProfile,
  } = formData;

  useEffect(() => {
    setFormData(CaseUtil.toCase('camelCase', userDetails));
  }, [userDetails]);

  const onClickHandler = useCallback((e, inputId) => {
    trackingMissingDetailsForm(e, inputId, styles.inputField);
  }, []);

  const postUserDetailsCallback = useCallback((status, msg) => {
    trackingSubmitMissingDetails(status, msg);
  }, []);

  // use this method on submission
  // replace the formData with the combination of userDetails & the field values
  // inputted by the user
  const onSubmit = useCallback((e) => {
    e.preventDefault();
    const data = {
      experience,
      linkedin_profile: linkedinProfile,
      orgname,
      position,
    };

    if (!isSubmitting) {
      setIsSubmitting(true);

      dispatch(submitUserDetails(data, postUserDetailsCallback))
        .then(() => {
          setIsSubmitting(false);
        })
        .catch(() => {
          setIsSubmitting(false);
        });
    }
  }, [
    dispatch, orgname, position,
    experience, linkedinProfile, isSubmitting,
    postUserDetailsCallback,
  ]);

  if (!showMissingDetailsModal) {
    return (
      <></>
    );
  }

  return (
    <Modal
      isOpen={showMissingDetailsModal}
      position={tablet ? 'bottom' : 'center'}
      className={styles.containerUi}
      backdropClassName={styles.backdropUi}
      containerClassName={styles.bodyUi}
      headerClassName={styles.crossHeader}
    >
      <GTMTracking
        component="button"
        gtmEventType="cta"
        gtmEventAction="click"
        gtmEventResult="qna_missing_details_modal_close"
        gtmEventCategory="alumni_directory"
        className={styles.icon}
        onClick={handleCloseModal}
        role="button"
        tabIndex={0}
        gtmEventData={{
          click_text: 'close_icon',
          click_type: 'qna_missing_details_close',
          click_section: 'qna_missing_details_modal',
        }}
      >
        X
      </GTMTracking>

      <div className={styles.mainContainer}>
        <div className={styles.submittedHeading}>
          <img
            className={styles.successIcon}
            src={successIcon}
            alt="right-tick"
          />
          <div>You have submitted your questions!</div>
        </div>

        <div className={styles.varificationPoints}>
          <div className={styles.iconBox}>
            <img src={tickIcon} alt="tick-icon" />
            <div>We will verify your questions and forward to the Alumni</div>
          </div>
          <div className={styles.iconBox}>
            <img src={tickIcon} alt="tick-icon" />
            <div>
              You will be notified via Email / Whatsapp
              when the questions are answered
            </div>
          </div>
        </div>

        <form className={styles.formContainer}>
          <div className={styles.formHeading}>
            These optional details will help the alumni give
            your precise answers
          </div>
          <div className={styles.detailsContainer}>
            <div className={styles.inputRow}>
              <div className={styles.inputContainer}>
                <label htmlFor="orgname">Where do you work?</label>
                <input
                  className={styles.inputField}
                  type="text"
                  id="orgname"
                  name="orgname"
                  value={formData.orgname}
                  onChange={handleChange}
                  onClick={(e) => onClickHandler(e, 'orgname')}
                />
              </div>

              <div className={styles.inputContainer}>
                <label htmlFor="position">What is current Role / domain?</label>
                <select
                  id="position"
                  className={styles.inputField}
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  onClick={(e) => onClickHandler(e, 'position')}
                >
                  <option disabled selected value="">Select an option</option>
                  {positions.map((jobPost) => (
                    <option key={jobPost} value={jobPost}>
                      {jobPost}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.inputRow}>
              <div className={styles.inputContainer}>
                <label htmlFor="experience">
                  How many years of experience?
                </label>
                <input
                  type="number"
                  id="experience"
                  className={styles.inputField}
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  onClick={(e) => onClickHandler(e, 'experience')}
                  required
                />
              </div>

              <div className={styles.inputContainer}>
                <label htmlFor="linkedinProfile">
                  Your LinkedIn Profile URL
                </label>
                <input
                  type="text"
                  id="linkedinProfile"
                  className={styles.inputField}
                  name="linkedinProfile"
                  value={formData.linkedinProfile}
                  onChange={handleChange}
                  onClick={(e) => onClickHandler(e, 'linkedinProfile')}
                  required
                />
              </div>
            </div>

            <GTMTracking
              component="button"
              gtmEventType="cta"
              gtmEventAction="click"
              gtmEventResult="qna_missing_details_submit"
              gtmEventCategory="alumni_directory"
              className={styles.submitButton}
              onClick={onSubmit}
              role="button"
              tabIndex={0}
              gtmEventData={{
                click_text: 'Submit',
                click_type: 'qna_missing_details_submit',
                click_section: 'qna_missing_details_modal',
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </GTMTracking>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default MissingDetails;
